import React,{useEffect,useState,useMemo,useRef} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';

const STAGES=[
  {
    label: 'Receiving your enquiry',
    icon: 'Inbox',
    color: 'blue',
    bgColor: 'bg-blue-500/20',
    borderColor: 'border-blue-500/30',
    iconColor: 'text-blue-400',
    progressThreshold: 0,
  },
  {
    label: 'Recording your selected website reference',
    icon: 'Bookmark',
    color: 'purple',
    bgColor: 'bg-purple-500/20',
    borderColor: 'border-purple-500/30',
    iconColor: 'text-purple-400',
    progressThreshold: 20,
  },
  {
    label: 'Preparing your customer confirmation',
    icon: 'FileText',
    color: 'cyan',
    bgColor: 'bg-cyan-500/20',
    borderColor: 'border-cyan-500/30',
    iconColor: 'text-cyan-400',
    progressThreshold: 40,
  },
  {
    label: 'Notifying BPA Solutions',
    icon: 'Bell',
    color: 'amber',
    bgColor: 'bg-amber-500/20',
    borderColor: 'border-amber-500/30',
    iconColor: 'text-amber-400',
    progressThreshold: 60,
  },
  {
    label: 'Confirming your enquiry record',
    icon: 'CheckCircle',
    waitingIcon: 'Clock',
    color: 'emerald',
    bgColor: 'bg-emerald-500/20',
    borderColor: 'border-emerald-500/30',
    iconColor: 'text-emerald-400',
    progressThreshold: 75,
  },
];

const StagedSubmitModal=({status,errorMessage,softTimeoutMessage,onClose})=> {
  const [progress,setProgress]=useState(0);
  const [displayStatus,setDisplayStatus]=useState('idle');
  const successAnimRef=useRef(null);

  useEffect(()=> {
    setDisplayStatus(status);
  },[status]);

  useEffect(()=> {
    if (status==='processing') {
      setProgress(0);
      const progressInterval=setInterval(()=> {
        setProgress((prev)=> {
          if (prev < 92) return prev + 2;
          return prev;
        });
      },400);
      return ()=> clearInterval(progressInterval);
    } else if (status==='success') {
      if (successAnimRef.current) clearTimeout(successAnimRef.current);
      successAnimRef.current=setTimeout(()=> {
        setProgress(100);
      },700);
      return ()=> {
        if (successAnimRef.current) clearTimeout(successAnimRef.current);
      };
    } else if (status==='error') {
      if (successAnimRef.current) clearTimeout(successAnimRef.current);
    }
  },[status]);

  useEffect(()=> {
    return ()=> {
      if (successAnimRef.current) clearTimeout(successAnimRef.current);
    };
  },[]);

  const getStageStatus=useMemo(()=> {
    return STAGES.map((stage,idx)=> {
      if (displayStatus==='success' && progress >=100) {
        return 'completed';
      }
      if (displayStatus==='error') {
        return idx < 4 ? 'completed' : 'inactive';
      }
      if (displayStatus==='processing') {
        if (progress >=stage.progressThreshold) {
          if (idx ===4) {
            return 'active';
          }
          return 'completed';
        }
        if (idx > 0 && progress >=STAGES[idx-1].progressThreshold) {
          return 'active';
        }
        if (idx ===0 && progress >=0) {
          return 'active';
        }
        return 'inactive';
      }
      return 'inactive';
    });
  },[progress,displayStatus]);

  const getStatusLabel=()=> {
    if (displayStatus==='success') return 'Enquiry confirmed';
    if (displayStatus==='error') return 'Submission error';
    if (progress >=92) return 'Confirming enquiry record';
    return 'Secure enquiry processing';
  };

  if (displayStatus==='idle') return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          exit={{opacity: 0}}
          className="absolute inset-0 bg-black/85 backdrop-blur-sm"
        />
        <motion.div
          initial={{opacity: 0,scale: 0.95,y: 10}}
          animate={{opacity: 1,scale: 1,y: 0}}
          exit={{opacity: 0,scale: 0.95,y: 10}}
          transition={{duration: 0.3,ease: "easeOut"}}
          className="relative w-full max-w-lg glass-panel p-8 rounded-3xl shadow-[0_30px_120px_rgba(37,99,235,0.35)] border border-white/15"
        >
          <div className="mb-6 flex justify-center">
            {displayStatus==='success' ? (
              <motion.div
                initial={{scale: 0,rotate: -180}}
                animate={{scale: 1,rotate: 0}}
                transition={{type: "spring",stiffness: 200,damping: 20}}
                className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center text-emerald-400 border border-emerald-500/30"
              >
                <SafeIcon name="Check" className="w-10 h-10" />
              </motion.div>
            ) : displayStatus==='error' ? (
              <motion.div
                initial={{scale: 0}}
                animate={{scale: 1}}
                className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center text-red-400 border border-red-500/30"
              >
                <SafeIcon name="X" className="w-10 h-10" />
              </motion.div>
            ) : (
              <div className="relative w-20 h-20">
                <svg className="w-20 h-20 -rotate-90" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="rgba(255,255,255,0.1)"
                    strokeWidth="8"
                  />
                  <motion.circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="url(#gradient)"
                    strokeWidth="8"
                    strokeLinecap="round"
                    initial={{strokeDasharray: "251.2",strokeDashoffset: "251.2"}}
                    animate={{strokeDashoffset: 251.2 - (251.2 * progress) / 100}}
                    transition={{duration: 0.3}}
                  />
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#3B82F6" />
                      <stop offset="100%" stopColor="#8B5CF6" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <motion.div
                    animate={{rotate: 360}}
                    transition={{duration: 2,ease: "linear",repeat: Infinity}}
                    className="w-8 h-8 border-4 border-blue-500/20 border-t-blue-500 rounded-full"
                  />
                </div>
              </div>
            )}
          </div>

          {displayStatus==='success' ? (
            <>
              <h3 className="text-xl font-bold text-white mb-2 text-center">
                Thanks — your enquiry has been received.
              </h3>
              <div className="text-sm text-slate-300 mt-4 space-y-4 leading-relaxed text-left bg-white/5 p-5 rounded-2xl border border-white/10">
                <p>
                  We will review your business,service area and selected website reference before getting in touch.
                </p>
                <p>
                  We normally respond between 8:00am and 6:00pm Sydney time and will try to contact you during your nominated preferred time.
                </p>
              </div>
              <button
                onClick={onClose}
                className="mt-6 w-full py-3.5 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium transition-colors border border-white/10"
              >
                Close
              </button>
            </>
          ) : displayStatus==='error' ? (
            <>
              <h3 className="text-xl font-bold text-white mb-2 text-center">
                Submission Error
              </h3>
              <div className="text-sm text-red-200 mt-4 leading-relaxed p-5 rounded-2xl border border-red-500/20 bg-red-500/10">
                <p>{errorMessage || 'An error occurred while processing your request. Please try again or contact us directly.'}</p>
              </div>
              <button
                onClick={onClose}
                className="mt-6 w-full py-3.5 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium transition-colors border border-white/10"
              >
                Go Back
              </button>
            </>
          ) : (
            <>
              <h3 className="text-xl font-bold text-white mb-1 text-center">
                Processing Enquiry
              </h3>
              {softTimeoutMessage && (
                <p className="text-sm text-amber-400 text-center mb-4">
                  {softTimeoutMessage}
                </p>
              )}
              <div className="space-y-3 mt-6">
                {STAGES.map((stage,idx)=> {
                  const stageStatus=getStageStatus[idx];
                  const IconComponent=SafeIcon;
                  const showWaitingIcon=stage.waitingIcon && stageStatus==='active' && idx===4;
                  const iconName=showWaitingIcon ? stage.waitingIcon : stage.icon;
                  return (
                    <motion.div
                      key={idx}
                      initial={{opacity: 0,x: -10}}
                      animate={{opacity: 1,x: 0}}
                      transition={{delay: idx * 0.1}}
                      className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                        stageStatus !=='inactive'
                          ? `${stage.bgColor} ${stage.borderColor}`
                          : 'bg-white/5 border-white/5'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                        stageStatus==='completed'
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                          : stageStatus==='active'
                          ? `${stage.bgColor} ${stage.iconColor} border ${stage.borderColor}`
                          : 'bg-white/5 text-slate-500 border border-white/5'
                      }`}>
                        {stageStatus==='completed' ? (
                          <SafeIcon name="Check" className="w-5 h-5" />
                        ) : stageStatus==='active' && idx===4 ? (
                          <motion.div
                            animate={{rotate: 360}}
                            transition={{duration: 2,ease: "linear",repeat: Infinity}}
                          >
                            <IconComponent name={iconName} className="w-5 h-5" />
                          </motion.div>
                        ) : (
                          <IconComponent name={iconName} className="w-5 h-5" />
                        )}
                      </div>
                      <span className={`text-sm font-medium ${
                        stageStatus !=='inactive'
                          ? 'text-white'
                          : 'text-slate-500'
                      }`}>
                        {stage.label}
                      </span>
                      {stageStatus==='active' && idx !==4 && (
                        <motion.div
                          className="ml-auto w-2 h-2 rounded-full"
                          style={{backgroundColor: stage.color==='blue' ? '#3B82F6' : stage.color==='purple' ? '#8B5CF6' : stage.color==='cyan' ? '#06B6D4' : stage.color==='amber' ? '#F59E0B' : '#10B981'}}
                          animate={{scale: [1,1.2,1]}}
                          transition={{duration: 1.5,repeat: Infinity}}
                        />
                      )}
                      {stageStatus==='active' && idx ===4 && (
                        <motion.div
                          className="ml-auto w-2 h-2 rounded-full bg-emerald-400"
                          animate={{scale: [1,1.3,1],opacity: [0.6,1,0.6]}}
                          transition={{duration: 2,repeat: Infinity}}
                        />
                      )}
                    </motion.div>
                  );
                })}
              </div>
              <div className="mt-6 pt-4 border-t border-white/10">
                <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
                  <span>Status</span>
                  <span className="font-medium text-slate-300">
                    {getStatusLabel()}
                  </span>
                </div>
                <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
                    initial={{width: 0}}
                    animate={{width: `${progress}%`}}
                    transition={{duration: 0.3}}
                  />
                </div>
                <div className="text-center text-xs text-slate-400 mt-2">
                  {progress < 100 ? `${progress}%` : 'Complete'}
                </div>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default StagedSubmitModal;